import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Palette } from 'core/config';
import {
    VerificationPending,
    InternalError,
    Typeform,
    Tile,
    productsAndServices,
    moneyTile
} from './components';
import { Spinner } from 'view/components/layout';
import { logout, useAuthorization, useAuthorizationDispatch } from 'core/context';
import { getApplicant, getApplications, getUser } from 'core/api';
import { Data, IUser } from 'core/utilities';
import { useToast } from 'core/toast';

const TYPEFORM_PATH = {
    'realty': 'XexbMyOX',
    'wheel': 'ty79jANs'
} as Data;

export const DashboardPage: React.FunctionComponent = () => 
{
    const history = useHistory();
    const { setToastMessage } = useToast();
    const authState = useAuthorization();
    const dispatch = useAuthorizationDispatch();
    const [ loading, setLoading ] = useState<boolean>(true);
    const [ typeformId, setTypeformId ] = useState<string>('');
    const [ user, setUser ] = useState<IUser | undefined>();
    const [ status, setStatus ] = useState<boolean>(false);
    const [ applicantStatus, setApplicantStatus ] = useState<string>('');
    const [ hasConsumerReport, setHasConsumerReport ] = useState<boolean>(false);
    const [ applicationStatus, setApplicationStatus ] = useState<string>('');
    const onTypeformClose = () => setTypeformId('');

    useEffect(() => 
    {
        let isMounted = true;
        let fetchSucceeded = false;
        let fetchTimedOut = false;

        (async () => 
        {
            const {
                user,
                success
            } = await getUser(authState);

            const { application } = await getApplications(authState);
            const { applicant } = await getApplicant(authState);

            if (!fetchTimedOut && isMounted) 
            {
                fetchSucceeded = true;
                setUser(user);
                setStatus(success);
                setLoading(false);
                setApplicantStatus(applicant?.status ?? '');
                setHasConsumerReport(applicant?.hasConsumerReport ?? false);
                setApplicationStatus(application?.status ?? '');
            }
        })();

        // Notify & log user out if fetching their profile takes longer than 90s
        const timeoutId = setTimeout(() => 
        {
            if (!fetchSucceeded) 
            {
                fetchTimedOut = true;
                setToastMessage();
                logout({ dispatch });
                history.push('/login');
            }
        }, 1000 * 90);

        return () => 
        {
            isMounted = false;
            clearTimeout(timeoutId);
        };
    }, [ setLoading ]);

    if (loading) return <Spinner css='position: absolute; margin-top: 50px;' />;
    if (!status) return <InternalError />;
    if (!user?.verified) return <VerificationPending />;

    const typeFormURL = typeformId !== 'wheel'
        ? `${TYPEFORM_PATH[typeformId]}`
        : `${TYPEFORM_PATH[typeformId]}#email=${user?.email}&name=${user?.fullname}`;

    return (
        <Wrapper isLoading={loading}>
            {typeformId && (
                <Typeform
                    url={`https://form.typeform.com/to/${typeFormURL}`}
                    id={typeformId}
                    currentId={typeformId}
                    onClose={onTypeformClose}
                />
            )}
            <Container>
                <Section>
                    <Welcome>Welcome Back!</Welcome>
                    <Username>{user?.fullname}</Username>
                    <Description>Check out our line of halal financial products! Click one to get started.</Description>
                    <LeftRight>
                        <Left>
                            <TilesHeader>Open a Manzil Investment Account</TilesHeader>
                            <Tile
                                isLarge
                                userDetails={{
                                    ...user,
                                    applicantStatus,
                                    applicationStatus,
                                    hasConsumerReport
                                }}
                                setTypeformId={setTypeformId}
                                {...moneyTile}
                            />
                        </Left>
                        <Right>
                            <TilesHeader>
                                Other Products and Services
                            </TilesHeader>
                            <ProductTiles>
                                {productsAndServices.map((product, id) => (
                                    <Tile
                                        key={id}
                                        userDetails={{
                                            ...user,
                                            applicantStatus,
                                            applicationStatus,
                                            hasConsumerReport
                                        }}
                                        setTypeformId={setTypeformId}
                                        {...product}
                                    />
                                ))}
                            </ProductTiles>
                        </Right>
                    </LeftRight>
                </Section>
            </Container>
        </Wrapper>
    );
};

const Wrapper = styled.div<{ isLoading: boolean; }>`
    width: 100%;
    background-color: ${Palette.EGGSHELL_WHITE};
    opacity: ${props => props.isLoading ? 0 : 1};
    transition: opacity 300ms ease 0.5s, visibility 0s linear 0s;
`;

const Container = styled.div`
    background-color: ${Palette.EGGSHELL_WHITE};
    width: 100%;
    display: flex;
`;

const Section = styled.div`
    padding: 116px 93px;
    width: 100%;

    @media only screen and (max-width: 600px) {
        padding: 10px 30px;
    }

    @media only screen and (max-width: 400px) {
        padding: 10px 15px;
    }
`;

const LeftRight = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 475px;
    column-gap: 6rem;

    @media only screen and (max-width: 1329px) {
        grid-template-columns: 2fr 382px;

        & >*:first-child {
            margin-bottom: 4rem;
        }
    }

    @media only screen and (max-width: 1050px) {
        grid-template-columns: 1fr;

        & >*:first-child {
            margin-bottom: 4rem;
        }
    }
`;

const Left = styled.div`
`;

const Right = styled.div`
`;

const Welcome = styled.p`
    text-align: left;
    font: normal normal normal 12px 'SofiaPro-Regular';
    color: ${Palette.BLUE};
    line-height: 1;
    margin-bottom: -27px;

    @media only screen and (max-width: 600px) {
        font-size: 15px;
        margin-bottom: -14px;
        margin-top: 110px;
    }

    @media only screen and (min-width: 1300px) {
        font-size: 14px;
    }

    @media only screen and (min-width: 1900px) {
        font-size: 16px;
    }
`;

const Username = styled.p`
    text-align: left;
    font: normal normal bold 37px 'SofiaPro-Bold';
    color: ${Palette.BLUE};
    line-height: 1;
    margin-bottom: 12px;
    font-weight: 500;

    @media only screen and (max-width: 600px) {
        font-size: 30px;
        margin-bottom: 1px;
    }
`;

const Description = styled.p`
    text-align: left;
    font: normal normal normal 10px 'SofiaPro-Regular';
    color: ${Palette.BLUE};
    line-height: 14px;
    margin-bottom: 52px;

    @media only screen and (max-width: 600px) {
        font-size: 15px;
        line-height: 16px;
        width: 345px;
        margin-bottom: 40px;
    }

    @media only screen and (min-width: 1300px) {
        font-size: 12px;
    }

    @media only screen and (min-width: 1900px) {
        font-size: 14px;
    }
`;

const ProductTiles = styled.div`
    display: flex;
    flex-direction:row;
    flex-flow: row wrap;
    width: 100%;

    @media only screen and (max-width: 600px)
    {
        width:120%;
    }
`;

const TilesHeader = styled.div`
    user-select: none;
    cursor: pointer;
    text-align: left;
    font: normal normal bold 27px 'SofiaPro-Regular';
    color: ${Palette.BLUE};
    line-height: 1;
    margin-bottom: 24px;

    @media only screen and (max-width: 600px) {
        font-size: 18px;
    }

    @media only screen and (min-width: 1200px) {
        font-size: 18px;
    }

    @media only screen and (min-width: 1329px) {
        font-size: 23px;
    }
`;